import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Radar, Wifi, WifiOff, LineChart as LineIcon } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip } from "recharts";

export default function PingPanel({ devices = [], onClose }) {
  const [host, setHost] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [devRes, setDevRes] = useState({});
  const [hist, setHist] = useState({});
  const loadHist = async (id, minutes) => {
    setHist((p) => ({ ...p, [id]: { loading: true, minutes } }));
    try {
      const { data } = await api.get(`/ping/history/${id}`, { params: { minutes } });
      const rows = (data || []).map((r) => ({ t: (r.ts || "").slice(11, 16), rtt: r.rtt_ms, up: r.up }));
      setHist((p) => ({ ...p, [id]: { rows, minutes } }));
    } catch { setHist((p) => ({ ...p, [id]: { rows: [], minutes } })); }
  };
  const toggleHist = (id) => {
    if (hist[id]) { setHist((p) => { const n = { ...p }; delete n[id]; return n; }); return; }
    loadHist(id, 360);
  };

  const doPing = async () => {
    const h = host.trim();
    if (!h) return toast.error("Podaj adres IP lub host");
    setBusy(true); setRes(null);
    try {
      const { data } = await api.post("/ping", { host: h, count: 4 });
      setRes(data);
      if (!data.alive) toast.error("Brak odpowiedzi");
      else toast.success(`Odpowiada${data.rtt_ms != null ? ` (${data.rtt_ms} ms)` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd pingowania"); } finally { setBusy(false); }
  };
  const pingDevice = async (d) => {
    setDevRes((p) => ({ ...p, [d.id]: { loading: true } }));
    try { const { data } = await api.post(`/ping/device/${d.id}`); setDevRes((p) => ({ ...p, [d.id]: data })); }
    catch (e) { setDevRes((p) => ({ ...p, [d.id]: { alive: false, error: e.response?.data?.detail || "Błąd" } })); }
  };
  const inputCls = "w-full bg-slate-900 border border-slate-700 rounded px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-cyan-500";
  const rline = (r) => (r ? (r.alive
    ? <span className="text-green-400">● online {r.rtt_ms != null ? `· ${r.rtt_ms} ms` : ""} {r.loss_pct != null ? `· strata ${r.loss_pct}%` : ""} {r.method ? `· ${r.method}` : ""}</span>
    : <span className="text-red-400">● brak odpowiedzi {r.error ? `· ${r.error}` : ""}</span>) : null);

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="ping-panel">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800">
        <Radar size={18} className="text-green-400" />
        <div className="font-bold flex-1">Ping</div>
        <button onClick={onClose} data-testid="ping-close" className="p-1.5 rounded hover:bg-slate-700"><X size={18} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="space-y-2">
          <div className="text-xs text-slate-400">Sprawdź dowolny adres IP lub host</div>
          <div className="flex gap-2">
            <input value={host} onChange={(e) => setHost(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doPing()}
              placeholder="np. 8.8.8.8 lub host.pl" data-testid="ping-host-input" className={inputCls} />
            <button onClick={doPing} disabled={busy} data-testid="ping-run-btn"
              className="shrink-0 bg-green-600 hover:bg-green-500 disabled:opacity-60 rounded px-4 text-sm font-semibold flex items-center gap-2">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Radar size={15} />} Ping
            </button>
          </div>
          {res && (
            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-sm" data-testid="ping-result">
              {rline(res)}
              {res.output && <pre className="mt-2 text-[11px] text-slate-400 whitespace-pre-wrap max-h-40 overflow-y-auto">{res.output}</pre>}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs text-slate-400 mb-2">Urządzenia z monitoringu ({devices.length})</div>
          <div className="space-y-2" data-testid="ping-device-list">
            {devices.length === 0 && <div className="text-slate-500 text-sm">Brak urządzeń w monitoringu.</div>}
            {devices.map((d) => {
              const r = devRes[d.id];
              return (
                <div key={d.id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-3" data-testid={`ping-dev-${d.id}`}>
                  <div className="flex items-center gap-2">
                    {r?.alive ? <Wifi size={15} className="text-green-400" /> : r && !r.loading ? <WifiOff size={15} className="text-red-400" /> : <Radar size={15} className="text-slate-400" />}
                    <span className="font-semibold text-slate-200 flex-1 truncate">{d.name}</span>
                    <span className="text-xs text-slate-500 font-mono">{d.host}</span>
                    <button onClick={() => toggleHist(d.id)} data-testid={`ping-hist-btn-${d.id}`}
                      className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1 flex items-center gap-1">
                      <LineIcon size={12} /> Historia
                    </button>
                    <button onClick={() => pingDevice(d)} data-testid={`ping-dev-btn-${d.id}`}
                      className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1 flex items-center gap-1">
                      {r?.loading ? <Loader2 size={12} className="animate-spin" /> : <Radar size={12} />} Ping
                    </button>
                  </div>
                  {r && !r.loading && <div className="mt-1.5 text-xs">{rline(r)}</div>}
                  {hist[d.id] && (
                    <div className="mt-2" data-testid={`ping-hist-${d.id}`}>
                      <div className="flex gap-1 mb-1.5" data-testid={`ping-hist-range-${d.id}`}>
                        {[["1h", 60], ["6h", 360], ["24h", 1440]].map(([lbl, m]) => (
                          <button key={m} onClick={() => loadHist(d.id, m)} data-testid={`ping-hist-range-${d.id}-${m}`}
                            className={`text-[11px] rounded px-2 py-0.5 ${(hist[d.id].minutes || 360) === m ? "bg-green-600 text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"}`}>{lbl}</button>
                        ))}
                      </div>
                      {hist[d.id].loading ? (
                        <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Ładowanie historii…</div>
                      ) : (hist[d.id].rows || []).length === 0 ? (
                        <div className="text-xs text-slate-500">Brak danych historycznych (poczekaj na kolejne cykle monitoringu).</div>
                      ) : (
                        <>
                          <div className="text-[11px] text-slate-400 mb-1">Czas odpowiedzi [ms] · dostępność {Math.round((hist[d.id].rows.filter((x) => x.up).length / hist[d.id].rows.length) * 100)}%</div>
                          <div style={{ width: "100%", height: 120 }}>
                            <ResponsiveContainer>
                              <LineChart data={hist[d.id].rows} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                                <XAxis dataKey="t" tick={{ fontSize: 9, fill: "#64748b" }} minTickGap={24} />
                                <YAxis tick={{ fontSize: 9, fill: "#64748b" }} width={34} />
                                <RTooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 11 }} />
                                <Line type="monotone" dataKey="rtt" stroke="#22c55e" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="flex gap-0.5 mt-1" title="Dostępność (zielone=online, czerwone=brak)">
                            {hist[d.id].rows.map((x, i) => <span key={i} className={`h-2 flex-1 rounded-sm ${x.up ? "bg-green-500" : "bg-red-500"}`} />)}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
